import type { MediaAsset, MediaTransformRequest } from '@recipes/contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@recipes/ui';
import { RotateCcw, RotateCw, Undo2 } from 'lucide-react';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';

type Rotation = MediaTransformRequest['rotate'];
interface CropBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ImageEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetId: string;
  /** Full-size image URL to edit. */
  src: string;
  altText: string | null;
  onSaved: (asset: MediaAsset) => void;
}

const MAX_CANVAS = 640;

/**
 * Client-side crop / rotate UI. The actual transformation happens on the server
 * (POST /media/:id/transform) so the stored asset and its variants stay consistent.
 * Crop coordinates are expressed in the pixel space of the *rotated* image.
 */
export function ImageEditorDialog({
  open,
  onOpenChange,
  assetId,
  src,
  altText,
  onSaved,
}: ImageEditorDialogProps) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [rotate, setRotate] = useState<Rotation>(0);
  const [crop, setCrop] = useState<CropBox | null>(null);
  const [alt, setAlt] = useState(altText ?? '');
  const [saving, setSaving] = useState(false);
  const drag = useRef<{
    startX: number;
    startY: number;
    mode: 'draw' | 'move';
    origin: CropBox | null;
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    setRotate(0);
    setCrop(null);
    setAlt(altText ?? '');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setImage(img);
    img.onerror = () => setImage(null);
    img.src = src;
  }, [open, src, altText]);

  // Rotated image size in source pixels.
  const rotated = useMemo(() => {
    if (!image) return { width: 1, height: 1 };
    const swap = rotate === 90 || rotate === 270;
    return {
      width: swap ? image.naturalHeight : image.naturalWidth,
      height: swap ? image.naturalWidth : image.naturalHeight,
    };
  }, [image, rotate]);
  const scale = Math.min(1, MAX_CANVAS / Math.max(rotated.width, rotated.height));
  const canvasWidth = Math.max(1, Math.round(rotated.width * scale));
  const canvasHeight = Math.max(1, Math.round(rotated.height * scale));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.save();
    ctx.translate(canvasWidth / 2, canvasHeight / 2);
    ctx.rotate((rotate * Math.PI) / 180);
    ctx.scale(scale, scale);
    ctx.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);
    ctx.restore();
  }, [image, rotate, scale, canvasWidth, canvasHeight]);

  const clampBox = (box: CropBox): CropBox => {
    const x = Math.max(0, Math.min(box.x, rotated.width - 1));
    const y = Math.max(0, Math.min(box.y, rotated.height - 1));
    return {
      x,
      y,
      width: Math.max(1, Math.min(box.width, rotated.width - x)),
      height: Math.max(1, Math.min(box.height, rotated.height - y)),
    };
  };

  const toSource = (event: ReactPointerEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * rotated.width,
      y: ((event.clientY - rect.top) / rect.height) * rotated.height,
    };
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!image) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const p = toSource(event);
    const inside =
      crop &&
      p.x >= crop.x &&
      p.x <= crop.x + crop.width &&
      p.y >= crop.y &&
      p.y <= crop.y + crop.height;
    drag.current = { startX: p.x, startY: p.y, mode: inside ? 'move' : 'draw', origin: crop };
    if (!inside) setCrop(clampBox({ x: p.x, y: p.y, width: 1, height: 1 }));
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const p = toSource(event);
    if (d.mode === 'move' && d.origin) {
      setCrop(
        clampBox({
          ...d.origin,
          x: d.origin.x + (p.x - d.startX),
          y: d.origin.y + (p.y - d.startY),
        }),
      );
    } else {
      setCrop(
        clampBox({
          x: Math.min(d.startX, p.x),
          y: Math.min(d.startY, p.y),
          width: Math.abs(p.x - d.startX),
          height: Math.abs(p.y - d.startY),
        }),
      );
    }
  };
  const onPointerUp = () => {
    if (crop && (crop.width < 8 || crop.height < 8)) setCrop(null);
    drag.current = null;
  };

  const updateCropField = (field: keyof CropBox, value: string) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return;
    setCrop((c) =>
      clampBox({
        ...(c ?? { x: 0, y: 0, width: rotated.width, height: rotated.height }),
        [field]: Math.round(n),
      }),
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const asset = await api.media.transform(assetId, {
        rotate,
        crop: crop
          ? {
              x: Math.round(crop.x),
              y: Math.round(crop.y),
              width: Math.round(crop.width),
              height: Math.round(crop.height),
            }
          : null,
        altText: alt.trim() || null,
      });
      onSaved(asset);
      onOpenChange(false);
    } catch (error) {
      toastError(error, t('editor.images.transformFailed'));
    } finally {
      setSaving(false);
    }
  };

  const percent = (v: number, total: number) => `${(v / total) * 100}%`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent fullscreenOnMobile closeLabel={t('common.close')} className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('editor.images.editTitle')}</DialogTitle>
          <DialogDescription>{t('editor.images.editHint')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setRotate(((rotate + 270) % 360) as Rotation)}
          >
            <RotateCcw aria-hidden /> {t('editor.images.rotateLeft')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setRotate(((rotate + 90) % 360) as Rotation)}
          >
            <RotateCw aria-hidden /> {t('editor.images.rotateRight')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setCrop(null);
              setRotate(0);
            }}
          >
            <Undo2 aria-hidden /> {t('editor.images.reset')}
          </Button>
        </div>
        <div
          className="relative mx-auto max-h-[60vh] touch-none select-none overflow-hidden rounded-md bg-muted"
          style={{
            width: canvasWidth,
            maxWidth: '100%',
            aspectRatio: `${canvasWidth} / ${canvasHeight}`,
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          role="img"
          aria-label={t('editor.images.cropArea')}
        >
          <canvas ref={canvasRef} className="block size-full" />
          {crop && (
            <div
              className="pointer-events-none absolute border-2 border-primary bg-primary/10 shadow-[0_0_0_9999px_color-mix(in_oklch,var(--color-scrim)_45%,transparent)]"
              style={{
                left: percent(crop.x, rotated.width),
                top: percent(crop.y, rotated.height),
                width: percent(crop.width, rotated.width),
                height: percent(crop.height, rotated.height),
              }}
            />
          )}
        </div>
        <fieldset className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <legend className="mb-1 text-sm font-medium">{t('editor.images.cropFields')}</legend>
          {(['x', 'y', 'width', 'height'] as const).map((field) => (
            <div key={field}>
              <Label htmlFor={`crop-${field}`} className="text-xs">
                {t(`editor.images.crop.${field}`)}
              </Label>
              <Input
                id={`crop-${field}`}
                type="number"
                inputMode="numeric"
                min={0}
                value={
                  crop
                    ? Math.round(crop[field])
                    : field === 'width'
                      ? rotated.width
                      : field === 'height'
                        ? rotated.height
                        : 0
                }
                onChange={(e) => updateCropField(field, e.target.value)}
              />
            </div>
          ))}
        </fieldset>
        <div>
          <Label htmlFor="image-alt" className="mb-1 block">
            {t('editor.images.altText')}
          </Label>
          <Input
            id="image-alt"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            maxLength={300}
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={() => void save()} loading={saving} disabled={!image}>
            {t('editor.images.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
